import { useEffect, useState } from "react";
import { AppShell } from "@/components/AppShell";
import { api, formatApiError } from "@/lib/api";
import { ROLE_LABELS } from "@/context/AuthContext";
import { PageHeader, Empty, inr } from "@/components/ui-kit";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Banknote, Play, Pencil, CheckCircle2 } from "lucide-react";

function SalaryDialog({ staff, onClose, onSaved }) {
  const [s, setS] = useState({ basic: 0, allowances: 0, deductions: 0 });
  useEffect(() => { if (staff) setS(staff.salary || { basic: 0, allowances: 0, deductions: 0 }); }, [staff]);
  async function save() {
    try { await api.put(`/payroll/salary/${staff.id}`, { basic: Number(s.basic), allowances: Number(s.allowances), deductions: Number(s.deductions) }); toast.success("Salary saved"); onSaved(); onClose(); }
    catch (e) { toast.error(formatApiError(e)); }
  }
  return (
    <Dialog open={!!staff} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent>
        <DialogHeader><DialogTitle className="font-display">Salary • {staff?.name}</DialogTitle></DialogHeader>
        <div className="grid grid-cols-3 gap-3">
          {["basic", "allowances", "deductions"].map((k) => (
            <div key={k}><Label className="capitalize">{k}</Label><Input data-testid={`salary-${k}`} type="number" min={0} value={s[k]} onChange={(e) => setS({ ...s, [k]: e.target.value })} className="mt-1" /></div>
          ))}
        </div>
        <div className="text-sm text-slate-600">Net pay <b className="text-slate-900">{inr(Number(s.basic) + Number(s.allowances) - Number(s.deductions))}</b> / month</div>
        <Button data-testid="salary-save" onClick={save} className="btn-brand">Save salary</Button>
      </DialogContent>
    </Dialog>
  );
}

export default function PayrollPage() {
  const [staff, setStaff] = useState([]);
  const [slips, setSlips] = useState([]);
  const [month, setMonth] = useState(new Date().toISOString().slice(0, 7));
  const [editing, setEditing] = useState(null);

  const loadStaff = () => api.get("/payroll/staff").then((r) => setStaff(r.data)).catch(() => {});
  const loadSlips = () => api.get("/payroll/payslips", { params: { month } }).then((r) => setSlips(r.data)).catch(() => {});
  useEffect(() => { loadStaff(); }, []);
  useEffect(() => { loadSlips(); }, [month]); // eslint-disable-line react-hooks/exhaustive-deps

  async function run() {
    try { const { data } = await api.post("/payroll/run", { month }); toast.success(`${data.created} payslip(s) generated`); loadSlips(); }
    catch (e) { toast.error(formatApiError(e)); }
  }
  async function markPaid(id) { await api.patch(`/payroll/payslips/${id}/paid`); loadSlips(); }
  const total = slips.reduce((a, b) => a + b.net, 0);
  const paid = slips.filter((p) => p.status === "paid").reduce((a, b) => a + b.net, 0);

  return (
    <AppShell>
      <PageHeader eyebrow="HR" title="Staff & Payroll" subtitle="Set salaries, generate monthly payslips and mark them paid." />
      <Tabs defaultValue="payslips">
        <TabsList className="bg-violet-100">
          <TabsTrigger data-testid="tab-payslips" value="payslips">Payslips</TabsTrigger>
          <TabsTrigger data-testid="tab-salaries" value="salaries">Salaries</TabsTrigger>
        </TabsList>
        <TabsContent value="payslips" className="mt-5 space-y-4">
          <div className="flex flex-wrap items-end gap-3">
            <div><Label className="text-xs">Month</Label><Input data-testid="payroll-month" type="month" value={month} onChange={(e) => setMonth(e.target.value)} className="mt-1 bg-white w-44" /></div>
            <Button data-testid="run-payroll-btn" onClick={run} className="btn-brand"><Play size={15} className="mr-1" /> Generate payslips</Button>
            <div className="ml-auto text-sm text-slate-600">Total <b className="text-slate-900">{inr(total)}</b> • Paid <b className="text-emerald-700">{inr(paid)}</b></div>
          </div>
          {slips.length === 0 ? <Empty testid="payslips-empty" icon={Banknote} text="No payslips for this month. Set salaries, then generate." /> : (
            <div className="bg-white rounded-2xl shadow-sm overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-violet-50 text-slate-700"><tr><th className="text-left px-4 py-3">Staff</th><th className="text-right px-3">Basic</th><th className="text-right px-3">Allow.</th><th className="text-right px-3">Deduct.</th><th className="text-right px-3">Net</th><th className="px-3">Status</th><th /></tr></thead>
                <tbody className="divide-y divide-slate-100">
                  {slips.map((p) => (
                    <tr key={p.id} data-testid={`payslip-${p.id}`}>
                      <td className="px-4 py-3"><div className="font-semibold text-slate-900">{p.name}</div><div className="text-xs text-slate-500">{ROLE_LABELS[p.role]}</div></td>
                      <td className="text-right px-3">{inr(p.basic)}</td><td className="text-right px-3">{inr(p.allowances)}</td><td className="text-right px-3">{inr(p.deductions)}</td>
                      <td className="text-right px-3 font-bold">{inr(p.net)}</td>
                      <td className="px-3 text-center"><Badge className={p.status === "paid" ? "bg-emerald-100 text-emerald-800 hover:bg-emerald-100" : "bg-amber-100 text-amber-800 hover:bg-amber-100"}>{p.status}</Badge></td>
                      <td className="px-3 text-right">{p.status !== "paid" && <Button data-testid={`mark-paid-${p.id}`} size="sm" variant="outline" onClick={() => markPaid(p.id)}><CheckCircle2 size={14} className="mr-1" /> Paid</Button>}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </TabsContent>
        <TabsContent value="salaries" className="mt-5">
          <div className="bg-white rounded-2xl shadow-sm divide-y divide-slate-100">
            {staff.map((s) => (
              <div key={s.id} data-testid={`staff-salary-${s.id}`} className="flex items-center gap-3 px-4 py-3">
                <div className="h-9 w-9 rounded-full brand-header text-white flex items-center justify-center font-semibold">{s.name.charAt(0)}</div>
                <div className="flex-1"><div className="font-semibold text-slate-900">{s.name}</div><div className="text-xs text-slate-500">{ROLE_LABELS[s.role]}</div></div>
                <div className="text-sm text-right">{s.salary ? <b>{inr(s.salary.basic + s.salary.allowances - s.salary.deductions)}</b> : <span className="text-slate-400">Not set</span>}</div>
                <Button data-testid={`edit-salary-${s.id}`} size="sm" variant="outline" onClick={() => setEditing(s)}><Pencil size={14} /></Button>
              </div>
            ))}
          </div>
        </TabsContent>
      </Tabs>
      <SalaryDialog staff={editing} onClose={() => setEditing(null)} onSaved={loadStaff} />
    </AppShell>
  );
}
